import '@/styles/global.css'
import AppContext from '@/AppContext'
import { wrapper } from '@/store'
import App from 'next/app'
import * as Sentry from '@sentry/nextjs'
import UAParser from 'ua-parser-js'
import { IntlProvider } from 'next-intl'

import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  AVAILABLE_ARR_LANGS,
  AVAILABLE_LANGS,
} from '@/common/constants/language'
import { addScriptToBody, getCookie } from '@/common/utils'
import {
  deleteCookie,
  getCookie as getCookieNext,
  setCookie,
} from 'cookies-next'
import { tariffApi, userApi } from '@/common/services/api'
import { setConfig } from 'store/configSlice'
import { setRuntimeConfig } from '@/common/services/runtimeConfig'

import {
  REGEXP_FILE_EXTENSION,
  REGEXP_LANGUAGE,
  REGEXP_TRAILING_SLASH,
} from '@/common/constants/regExp'
import { initGA, logPageView } from '@/analytics'
import { useRouter } from 'next/router'
import Head from 'next/head'
import 'react-tooltip/dist/react-tooltip.css'
import Preloader from '@/components/ui/Preloader'
import WrapperPage from '@/components/WrapperPage'
import { useDispatch, useSelector } from 'react-redux'
import { clear, setExperiments, setFirstRender } from '@/store/userSlice'
import previousUrl from 'hooks/usePreviousUrl'
import getConfig from 'next/config'
import { Inter } from 'next/font/google'
import { userAnalytics } from '@/common/services/userAnalytics'
import userLocationApi from '@/common/services/userLocationApi'
import 'intl-pluralrules'
import dynamic from 'next/dynamic'
import baseLogger from '@/common/services/BaseLogger'
import {
  HASH_KEY,
  PREV_URL,
  TOKEN_KEY,
  USER_LANG_KEY,
} from '@/common/constants/cookies'
import {
  LEGACY_CREATIVES_URL,
  PAGES_PROTECTED_FOR_AUTHORIZED_USERS,
} from '@/common/constants/pages'
import userLanguage from '@/common/services/userLanguage'
import { SessionProvider } from 'next-auth/react'
import Script from 'next/script'
import { userAmplitudeAnalytics } from '@/common/services/userAmplitudeAnalytics'
import { ANALYTIC_FIRST_SESSION, HOME_PAGE } from '@/common/constants/analytics'
import { SUBSCRIPTION_DEMO } from '@/common/constants/payments'
import format from 'date-fns/format'
import { COOKIE_MAX_AGE } from '@/common/constants/duration'
import * as amplitude from '@amplitude/analytics-browser'
import { ExperimentProvider } from '@/contexts/experimentContext'
import { COOKIE_KEY_USER_TEAM } from '@/common/services/api/utils/createAxiosInstance'
import { getDeviceId } from '@/common/libs/identity'
import { isbot } from 'isbot'
import { ExperimentServer } from '@/libs/expirement'
import normalizeExperiments from '@/common/utils/normalizeExperiment'

const Notifications = dynamic(() => import('@/components/common/Notifications'))
const LimitSession = dynamic(() => import('@/components/LimitSession'))
const inter = Inter({
  subsets: ['latin'],
})

let experiment = null

const BaseApp = ({ Component, pageProps }) => {
  const router = useRouter()
  const currentPath = useRef('')
  const { isLimitSession, needShowUserLimitSession, subscription } =
    useSelector((state) => state.user)
  const [isLoading, setIsLoading] = useState(true)
  const [isMounted, setMounted] = useState(false)
  const { user } = useSelector((state) => state.user)

  const { asPath, locale } = useRouter()
  const baseUrl = `https://${getConfig().publicRuntimeConfig.baseDomain}`
  const pathWithoutQueryParams = asPath.split('?')[0]
  const dispatch = useDispatch()
  const prevPath = previousUrl()

  const detectIsFirstUserSession = () => {
    if (!user.had_first_session) {
      userApi.updateFirstSession().catch((error) => {
        baseLogger.logger.error(error, 'Error updating first session')
      })
      userAmplitudeAnalytics.trackEvent(ANALYTIC_FIRST_SESSION)
    }
  }

  const getFullPathByLang = (lang) => {
    const correctLang = lang === 'default' ? '' : `/${lang}`
    return `${baseUrl}${correctLang}${pathWithoutQueryParams}`.replace(
      REGEXP_TRAILING_SLASH,
      ''
    )
  }

  const initFbPixel = () => {
    import('react-facebook-pixel')
      .then((x) => x.default)
      .then((ReactPixel) => {
        ReactPixel.init(getConfig().publicRuntimeConfig.fbPixelId)
        ReactPixel.pageView()
      })
  }

  const sendAnalytic = (url, referer) => {
    tariffApi
      .sendAnalytic({
        data: {
          url: `https://${getConfig().publicRuntimeConfig.baseDomain}${url}`,
          referer: referer,
          language: router.locale,
        },
      })
      .catch((e) => {
        baseLogger.logger.error(e, 'failed send analytic')
      })
  }

  const sendUserDeviceToSentry = () => {
    const parser = new UAParser()
    const agent = parser.setUA(navigator.userAgent)
    const device = agent.getDevice().model ?? agent.getOS().name
    const deviceOs = `${agent.getOS().name} ${agent.getOS().version}`

    Sentry.setTag('device', device)
    Sentry.setTag('os', deviceOs)
  }
  const zendeskLogin = (user) => {
    try {
      const zEInstance = window.zE
      if (!zEInstance) return

      zEInstance('webWidget', 'setLocale', locale)
      zEInstance('webWidget', 'updateSettings', {
        webWidget: {
          authenticate: {
            jwtFn: function (callback) {
              if (user?.support?.token) {
                callback(user.support.token)
                console.error(user.support.token, 'JWT Token')
              } else {
                console.error(
                  'JWT Token is missing for Zendesk authentication.'
                )
              }
            },
          },
        },
      })
    } catch (e) {
      console.error('Error during Zendesk authentication setup:', e)
    }
  }

  const triggerOpenZendeskAnalytic = () => {
    userAmplitudeAnalytics.trackEvent(`${HOME_PAGE}_page_help_clicked`, {
      sourcePage: currentPath.current,
    })
  }

  const onOpenZendesk = () => {
    const zEInstance = window.zE
    if (!zEInstance) return

    zEInstance('webWidget:on', 'open', triggerOpenZendeskAnalytic)
  }

  const zendeskLoading = ($node, user) => {
    const zEInstance = window.zE
    if (!zEInstance) return

    try {
      zendeskLogin(user)
    } catch (e) {
      $node.addEventListener('load', function () {
        zendeskLogin(user)
      })
    }

    $node.addEventListener('load', function () {
      zEInstance('messenger', 'loginUser', function (callback) {
        callback(user.support.token)
      })
    })
  }

  const setToCookieUtmSource = () => {
    if (!router.query?.utm_source) return
    setCookie('source', router.query.utm_source)
  }

  const redirectToLogin = async () => {
    if (!getCookie(TOKEN_KEY) && !getCookie(HASH_KEY)) return
    if (!getCookie(TOKEN_KEY) || !getCookie(HASH_KEY)) {
      await userApi.logout()
      dispatch(clear())
      deleteCookie(HASH_KEY, {
        domain: `.${getConfig().publicRuntimeConfig.baseDomain}`,
      })
      deleteCookie(TOKEN_KEY)
    }
  }

  const setCohortCookies = () => {
    const now = new Date()

    setCookie('cohort_day', format(now, 'dd'), {
      maxAge: COOKIE_MAX_AGE,
    })
    setCookie('cohort_week', format(now, 'ww'), {
      maxAge: COOKIE_MAX_AGE,
    })
    setCookie('cohort_month', format(now, 'MM'), {
      maxAge: COOKIE_MAX_AGE,
    })
    setCookie('cohort_year', format(now, 'yyyy'), {
      maxAge: COOKIE_MAX_AGE,
    })
  }

  const getCohortCookies = () => {
    return {
      cohort_day: getCookieNext('cohort_day'),
      cohort_week: getCookieNext('cohort_week'),
      cohort_month: getCookieNext('cohort_month'),
      cohort_year: getCookieNext('cohort_year'),
    }
  }

  const getOrSetCohort = () => {
    const cohorts = getCohortCookies()

    if (
      !cohorts.cohort_day ||
      !cohorts.cohort_week ||
      !cohorts.cohort_month ||
      !cohorts.cohort_year
    ) {
      setCohortCookies()
      return getCohortCookies()
    }

    return cohorts
  }

  useEffect(() => {
    const cohorts = getOrSetCohort()

    userAmplitudeAnalytics.addUserProperties((event) => {
      event.set('cohort_day', cohorts.cohort_day)
      event.set('cohort_week', cohorts.cohort_week)
      event.set('cohort_month', cohorts.cohort_month)
      event.set('cohort_year', cohorts.cohort_year)
    })
  }, [])

  const handleBeforeUnload = () => {
    deleteCookie('userState')
  }

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      deleteCookie('userState')
    }
  }

  const amplitudeAuth = async () => {
    userAnalytics.track(['trackPageView'])

    if (!user) {
      userAmplitudeAnalytics.addUserProperties((event) => {
        event.set('registered_user', false)
      })
      return
    }

    const prevSid = amplitude.getSessionId()
    amplitude.setUserId(user.uuid)
    amplitude.setSessionId(prevSid)
    Sentry.setUser({
      id: user.uuid,
    })
    userAmplitudeAnalytics.addUserProperties((event) => {
      event.set('email_confirmed', user.is_verified)
      event.set('is_system_user', user.is_team_member)
      event.set('registered_user', true)
    })

    userAnalytics.track(['setUserId', user.uuid])

    detectIsFirstUserSession()
  }

  const isVisibleLimitSession = useMemo(
    () =>
      isLimitSession &&
      needShowUserLimitSession &&
      router.pathname !== '/login',
    [isLimitSession, router.pathname, needShowUserLimitSession]
  )

  useEffect(() => {
    const handleRouteChange = () => {
      localStorage.setItem(PREV_URL, window.location.href)
    }

    router.events.on('routeChangeStart', handleRouteChange)

    return () => {
      router.events.off('routeChangeStart', handleRouteChange)
    }
  }, [router.events])

  useEffect(() => {
    if (subscription) {
      userAmplitudeAnalytics.addUserProperties((event) => {
        event.set(
          'subscription_active',
          subscription.name !== SUBSCRIPTION_DEMO
        )
      })
    }
  }, [subscription])

  useEffect(() => {
    amplitudeAuth()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email])

  useEffect(() => {
    const zendeskScriptLoad = document.getElementById('ze-snippet')
    currentPath.current = `https://${
      getConfig().publicRuntimeConfig.baseDomain
    }${router.asPath}`

    if (zendeskScriptLoad) {
      zendeskLoading(zendeskScriptLoad, user)
      return
    }

    addScriptToBody(
      'ze-snippet',
      'https://static.zdassets.com/ekr/snippet.js?key=0562b0f1-83ae-42bf-a9b9-e720f3ff491a',
      () => {
        onOpenZendesk()
        zendeskLogin(user)
      }
    )
  }, [locale, user?.email, router.pathname])

  useEffect(() => {
    setIsLoading(true)
    setTimeout(() => {
      setIsLoading(false)
    }, 300)
  }, [locale])

  useEffect(() => {
    const userAgent = navigator.userAgent || ''

    setMounted(true)
    initGA()
    logPageView()
    userAnalytics.init()
    sendUserDeviceToSentry()
    setToCookieUtmSource()
    initFbPixel()
    redirectToLogin()
    userAmplitudeAnalytics.init(userAgent)

    window.addEventListener('beforeunload', handleBeforeUnload)
    document.addEventListener('visibilitychange', handleVisibilityChange)
  }, [])

  useEffect(() => {
    if (process.env.NEXT_PUBLIC_APP_ENV === 'development') return
    let prevUrl = null
    const referrer = document?.referrer
    const isReferrer = referrer || null

    if (isReferrer) prevUrl = referrer
    if (!isReferrer && prevPath)
      prevUrl = `https://${process.env.NEXT_PUBLIC_BASE_URL}${prevPath}`

    sendAnalytic(asPath, prevUrl)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asPath])

  const { runtimeConfig } = useSelector((state) => state.config)

  useEffect(() => {
    if (runtimeConfig) {
      setRuntimeConfig(runtimeConfig)
    }
  }, [runtimeConfig])

  return (
    <AppContext.Provider value={{}}>
      <Head>
        <link rel="canonical" href={getFullPathByLang(locale)} />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/_next_static/favicon/apple-touch-icon.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/_next_static/favicon/favicon-32x32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/_next_static/favicon/favicon-16x16.png"
        />
        <link rel="manifest" href="/_next_static/favicon/site.webmanifest" />
        <link
          rel="mask-icon"
          href="/_next_static/favicon/safari-pinned-tab.svg"
          color="#ffc40d"
        />
        <meta name="msapplication-TileColor" content="#ffc40d" />
        <meta name="theme-color" content="#ffffff" />
        {AVAILABLE_ARR_LANGS.map((langCode) => (
          <link
            key={langCode}
            rel="alternate"
            hrefLang={langCode}
            href={getFullPathByLang(langCode)}
          />
        ))}
      </Head>
      <Script
        strategy="lazyOnload"
        src="/static/socials/telegram-passport.js"
      />
      <SessionProvider session={pageProps.session}>
        <IntlProvider locale={router.locale} messages={pageProps.messages}>
          <ExperimentProvider value={experiment}>
            <main className={inter.className}>
              {isMounted && <Notifications />}
              <Preloader isLoading={isLoading} />
              <WrapperPage isLoading={isLoading}>
                {isVisibleLimitSession ? (
                  <LimitSession />
                ) : (
                  <div>
                    <Component {...pageProps} />
                    <div className="portal-container" id="portal" />
                  </div>
                )}
              </WrapperPage>
            </main>
          </ExperimentProvider>
        </IntlProvider>
      </SessionProvider>
    </AppContext.Provider>
  )
}

BaseApp.getInitialProps = wrapper.getInitialAppProps(
  (store) => async (context) => {
    const { ctx, router } = context
    let experimentData = {}
    const currentLanguage = router.locale
    const isDefaultLang = currentLanguage === 'default'
    const pageProps = await App.getInitialProps(context)
    const basePath = router.asPath
    const state = store.getState()
    const isFirstRender = state.user.isFirstRender
    const userLang =
      getCookieNext(USER_LANG_KEY, {
        req: ctx.req,
        res: ctx.res,
      }) ?? currentLanguage

    // @TODO: Remove after creating the /apps page
    const isLegacyPage = LEGACY_CREATIVES_URL.some((page) => {
      if (page === '/apps') {
        return basePath === '/apps'
      }

      return basePath.startsWith(page)
    })
    baseLogger.setPageUrl(router.pathname)

    if (typeof window === 'undefined') {
      userLocationApi.setIpCountry(ctx.req.headers['cf-ipcountry'])
      userLocationApi.setConnectionIP(ctx.req.headers['cf-connecting-ip'])
    }

    if (!isDefaultLang) {
      userLanguage.setLanguage(currentLanguage)
    }

    if (
      !ctx.req ||
      (!isFirstRender && !isLegacyPage && !isDefaultLang) ||
      REGEXP_FILE_EXTENSION.test(ctx.req?.url)
    ) {
      return {
        ...pageProps,
      }
    }

    if (isFirstRender) {
      await store.dispatch(setFirstRender(false))
    }

    baseLogger.setPageUrl(router.pathname)
    const cookies = ctx.req.headers?.cookie ?? ''
    const preferBrowserLanguage = ctx.req?.headers['accept-language']?.slice(
      0,
      2
    )
    const detectLanguage = AVAILABLE_LANGS[preferBrowserLanguage] ?? 'en'
    const cookieUserLang = getCookie(cookies, USER_LANG_KEY)
    const userToken = ctx.req.cookies.token
    const isAuth = userToken && ctx.req.cookies.hash
    let userInfo = null
    let isUserLanguage = userLang && userLang === currentLanguage
    const userAgent = ctx.req.headers['user-agent'] || ''

    const fetchUserInfo = async () => {
      try {
        if (!userToken) {
          baseLogger.logger.warn('No token found in cookies')
        } else {
          baseLogger.logger.info({ token: userToken }, 'Token from cookie')
        }

        userInfo = await userApi.getInfo({ token: userToken })

        Sentry.setUser({ id: userInfo.uuid })
        baseLogger.setUserId(userInfo.uuid)
        isUserLanguage = currentLanguage === userInfo?.language
      } catch (e) {
        baseLogger.logger.error('Failed to fetch userInfo')
      }
    }

    if (userToken) {
      await fetchUserInfo()
    }

    if (ctx.res) {
      setCookie(
        COOKIE_KEY_USER_TEAM,
        JSON.stringify({
          isTeamMember: userInfo?.is_team_member ?? false,
        }),
        {
          req: ctx.req,
          res: ctx.res,
        }
      )
    }

    if (process.env.AMPLITUDE_CLIENT_KEY && !isbot(userAgent)) {
      const deviceId = getDeviceId({
        req: ctx.req,
        res: ctx.res,
      })
      const userPayload = userInfo
        ? {
            user_id: userInfo.uuid,
            device_id: deviceId,
            name: userInfo.name,
          }
        : {
            device_id: deviceId,
          }

      experimentData = await ExperimentServer.fetchV2(userPayload)
      const cleanedExperiments = normalizeExperiments(experimentData)
      await store.dispatch(setExperiments(cleanedExperiments))
    }

    if (isAuth && !isDefaultLang && isLegacyPage) {
      let correctPath = basePath.replace(REGEXP_LANGUAGE, '/')

      ctx.res
        .writeHead(307, {
          location: correctPath,
        })
        .end()
    }

    if (isAuth && (isDefaultLang || !isUserLanguage)) {
      const language = userInfo?.language ?? detectLanguage
      userLanguage.setLanguage(language)
    } else if (cookieUserLang && cookieUserLang !== currentLanguage) {
      userLanguage.setLanguage(cookieUserLang)
    } else if (detectLanguage !== currentLanguage && !cookieUserLang) {
      userLanguage.setLanguage(detectLanguage)
    }

    if (
      (isAuth &&
        PAGES_PROTECTED_FOR_AUTHORIZED_USERS.includes(router.pathname)) ||
      (isAuth && isLegacyPage) ||
      router.pathname === '/r/[slug]'
    ) {
      return {
        ...pageProps,
        allTests: experimentData,
      }
    }

    if (isDefaultLang || !isUserLanguage) {
      ctx.res
        .writeHead(307, {
          location: `/${userLanguage.lang}${basePath}`,
        })
        .end()
    }

    if (ctx.req) {
      const runtimeConfig = {
        clientApiUrl: process.env.API_CLIENT_URL,
        serverApiUrl: process.env.API_SERVER_URL,
      }
      store.dispatch(setConfig(runtimeConfig))
      setRuntimeConfig(runtimeConfig)
    }

    return {
      ...pageProps,
      allTests: experimentData,
    }
  }
)
export default wrapper.withRedux(BaseApp)
